// The roadmap: one short column per stop, left to right. What just shipped, each upcoming release by target
// date, then Later (unscheduled features and improvements by priority). Features and improvements are listed;
// bug fixes are counted. Built from the release list and the backlog, so it always matches the plan. Items that
// went live in a hotfix sit with the release that announces them (or will: `announceWith`).
import { Link } from 'react-router-dom';
import { Check, Users } from 'lucide-react';
import { fmtDateTime } from '../../lib/format';
import { Card, Pill, ProgressBar, cx, type PillTone } from '../../ui';
import { isOpen, PRIORITY_RANK, type Item } from '../backlog/types';
import { fmtCalendarDate } from './types';

export type RoadmapRelease = {
  id: string; name: string; target_date: string | null; status: 'planned' | 'in_progress' | 'shipped' | 'cancelled';
  shipped_at: string | null; item_count: number; items_done: number;
};
type Stop = {
  key: string; title: string; when: string; tone: PillTone; label: string; href: string | null;
  done: number; total: number | null; items: Item[]; fixes: number; more: number;
};

const LATER_LIMIT = 8;
// Tasks and epics are the team's own work, not something a firm would look for on a roadmap.
const highlight = (i: Item) => (i.kind === 'feature' || i.kind === 'improvement') && i.status !== 'wont_do';

const liveHotfix = (i: Item) => i.release_kind === 'hotfix' && i.release_status === 'shipped';

/** The stops on the roadmap, in order: the latest shipped release, upcoming releases by target date, Later. */
export function roadmapStops(releases: RoadmapRelease[], items: Item[], announceWith: string | null = null): Stop[] {
  const inRelease = (id: string) => items.filter((i) => !i.archived_at && (i.release_id === id
    || (liveHotfix(i) && (i.release_announced_in_id ?? announceWith) === id)));
  const stop = (r: RoadmapRelease, tone: PillTone, label: string, when: string): Stop => {
    const all = inRelease(r.id);
    return {
      key: r.id, title: r.name, when, tone, label, href: `/admin/releases/${r.id}`, done: r.items_done, total: r.item_count,
      items: all.filter(highlight).sort((a, b) => Number(b.status === 'done') - Number(a.status === 'done') || a.sort_order - b.sort_order),
      fixes: all.filter((i) => i.kind === 'bug_fix').length, more: 0,
    };
  };
  const shipped = releases.filter((r) => r.status === 'shipped' && r.shipped_at)
    .sort((a, b) => b.shipped_at!.localeCompare(a.shipped_at!))[0];
  const upcoming = releases.filter((r) => r.status === 'planned' || r.status === 'in_progress')
    .sort((a, b) => (a.target_date ?? '9999').localeCompare(b.target_date ?? '9999') || a.name.localeCompare(b.name));
  const later = items.filter((i) => !i.release_id && isOpen(i) && highlight(i))
    .sort((a, b) => PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] || a.sort_order - b.sort_order);
  return [
    ...(shipped ? [stop(shipped, 'ok', 'Shipped', `Shipped ${fmtDateTime(shipped.shipped_at!)}`)] : []),
    ...upcoming.map((r) => stop(r, r.status === 'in_progress' ? 'info' : 'purple', r.status === 'in_progress' ? 'In progress' : 'Planned',
      r.target_date ? `Target ${fmtCalendarDate(r.target_date)}` : 'No target date')),
    {
      key: 'later', title: 'Later', when: 'Not scheduled yet', tone: 'neutral', label: 'Backlog', href: '/admin/backlog', done: 0, total: null,
      items: later.slice(0, LATER_LIMIT), fixes: 0, more: Math.max(0, later.length - LATER_LIMIT),
    },
  ];
}

export function Roadmap({ releases, items, announceWith }: { releases: RoadmapRelease[]; items: Item[]; announceWith: string | null }) {
  const stops = roadmapStops(releases, items, announceWith);
  return (
    <section aria-label="Roadmap" className="flex min-w-0 flex-col gap-2">
      {/* Left to right in time; with many releases the row scrolls sideways inside itself, not the page. */}
      <ol className="m-0 flex min-w-0 list-none gap-3 overflow-x-auto p-0 pb-2 [scroll-snap-type:x_proximity]">
        {stops.map((s) => (
          <li key={s.key} className="flex w-[min(280px,82vw)] flex-none flex-col [scroll-snap-align:start]">
            {/* The timeline: a dot per stop on a shared rule. */}
            <div aria-hidden className="mb-2 flex items-center">
              <span className={cx('h-3 w-3 flex-none rounded-full border-2 border-white shadow-[0_0_0_1.5px_rgb(var(--line-strong))]',
                s.label === 'Shipped' ? 'bg-ok-fg' : s.label === 'In progress' ? 'bg-brand' : 'bg-line-strong')} />
              <span className={cx('h-px flex-1 bg-line-strong', s.key === 'later' && 'bg-transparent bg-[linear-gradient(90deg,rgb(var(--line-strong))_50%,transparent_0)] bg-[length:8px_1px]')} />
            </div>
            <Card className={cx('flex flex-1 flex-col gap-2 p-4', s.key === 'later' && 'border-dashed')}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 flex-col leading-tight">
                  {s.href ? <Link to={s.href} className="truncate font-bold text-ink no-underline hover:underline">{s.title}</Link> : <span className="font-bold">{s.title}</span>}
                  <span className="text-xs text-muted">{s.when}</span>
                </div>
                <Pill tone={s.tone} dot>{s.label}</Pill>
              </div>
              {s.total !== null && s.total > 0 && s.label !== 'Shipped' && (
                <ProgressBar pct={(s.done / s.total) * 100} label={`${s.done} of ${s.total} items done`} />
              )}
              {s.items.length === 0 ? <p className="m-0 text-[13px] text-muted">{s.key === 'later' ? 'Nothing waiting.' : 'No features yet.'}</p> : (
                <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                  {s.items.map((i) => (
                    <li key={i.id} className="flex items-start gap-2 text-[13px] leading-snug">
                      {i.status === 'done'
                        ? <Check aria-label="Done" size={14} className="mt-0.5 flex-none text-ok-fg" />
                        : <span aria-hidden className={cx('mt-[5px] h-2 w-2 flex-none rounded-full', i.kind === 'feature' ? 'bg-brand' : 'bg-line-strong')} />}
                      <Link to={`/admin/backlog/${i.id}`} className={cx('min-w-0 flex-1 text-ink no-underline hover:underline', i.status === 'done' && 'text-ink-2')}>{i.title}</Link>
                      {i.requests.length > 0 && (
                        <span title={`${i.requests.length} firm request${i.requests.length === 1 ? '' : 's'}`} className="inline-flex flex-none items-center gap-0.5 text-xs text-muted">
                          <Users aria-hidden size={12} />{i.requests.length}<span className="sr-only"> firm requests</span>
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {(s.fixes > 0 || s.more > 0) && (
                <p className="m-0 mt-auto text-xs text-muted">
                  {s.fixes > 0 && `+ ${s.fixes} bug fix${s.fixes === 1 ? '' : 'es'}`}
                  {s.more > 0 && <Link to="/admin/backlog">+ {s.more} more in the backlog</Link>}
                </p>
              )}
            </Card>
          </li>
        ))}
      </ol>
      <p className="m-0 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted">
        <span className="inline-flex items-center gap-1.5"><span aria-hidden className="h-2 w-2 rounded-full bg-brand" />Feature</span>
        <span className="inline-flex items-center gap-1.5"><span aria-hidden className="h-2 w-2 rounded-full bg-line-strong" />Improvement</span>
        <span className="inline-flex items-center gap-1.5"><Check aria-hidden size={12} className="text-ok-fg" />Done</span>
        <span className="inline-flex items-center gap-1.5"><Users aria-hidden size={12} />Firms that asked</span>
      </p>
    </section>
  );
}
